import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Stack,
  TextField,
  IconButton,
  Button,
  InputAdornment,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import TitleWithBack from '../Home/TitleWithBack';
import {  SESSION_USER_ID_KEY } from '../../Util/VMConstants';
import MUITypography from '../../Components/MUITypography';
import StorageFactory from '../../Util/StorageFactory';
import { Search, People, CheckCircle, Close } from '@mui/icons-material';
import {
  API_GET_FB_CATEGORIES_DATA,
  API_GET_SETTLED_CUSTOMERS,
  API_SUBMIT_FEEDBACK,
  API_ADD_UPDATE_FB_CATEGORY,
  API_DELETE_FB_CATEGORY_ID,
  API_ADD_SOCIAL_MEDIA_INFO,
  API_GET_SOCIAL_MEDIA_INFO,
  API_DELETE_SOCIAL_MEDIA_INFO,
} from '../../Util/URLConstants';
import { toast } from 'react-toastify';
import Loader from '../../Components/Loader';
import FeedbackTemplate from './FeedbackTemplate';
import { align_center, dataGridStyle } from '../../Styles/VMStyles';
import axios from 'axios';
import CompanyLogoUploader from '../EnquiryDetails/Invoice/CompanyLogoUploader';
import SocialLinkSection from './SocialLinkSection';
import ReusableDataGrid from '../../Components/ReusableDataGrid';
import { returnDate } from '../../Util/UtilHelper';

const today = new Date();
const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
const formatDate = (date) => date.toISOString().split("T")[0];

const DEFAULT_PLATFORMS = [
  { key: 'facebook', label: 'Facebook' },
  { key: 'twitter', label: 'Twitter' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'youtube', label: 'YouTube' },
  { key: 'others', label: 'Others' },
];

function isValidUrl(value) {
  if (!value) return false;
  // simple URL validator (allows http(s) and simple domain/path)
  try {
    const u = new URL(value);
    return ['http:', 'https:'].includes(u.protocol);
  } catch (e) {
    return false;
  }
}

const mapSocialMediaFromApi = (item) => {
  const apiName = (item.name || '').trim();
  const platformMatch = DEFAULT_PLATFORMS.find(
    (p) => p.key !== 'others' && p.label.toLowerCase() === apiName.toLowerCase()
  );
  if (platformMatch) {
    return {
      id: item.id,
      platform: platformMatch.key,
      customPlatform: '',
      url: item.link || '',
    };
  }
  return {
    id: item.id,
    platform: 'others',
    customPlatform: apiName,
    url: item.link || '',
  };
};

const emptySocialDraft = () => ({
  platform: 'Facebook',
  customPlatform: '',
  url: '',
});

const apiToSectionFormat = (item) => {
  const ui = mapSocialMediaFromApi(item);
  const label = DEFAULT_PLATFORMS.find((p) => p.key === ui.platform);
  return {
    id: ui.id,
    platform: ui.platform === 'others' ? 'Other' : (label?.label || 'Other'),
    customPlatform: ui.customPlatform || '',
    url: ui.url || '',
  };
};

const sectionToStorageFormat = (link) => {
  if (link.platform === 'Other') {
    return {
      id: link.id,
      platform: 'others',
      customPlatform: link.customPlatform || '',
      url: link.url,
    };
  }
  const match = DEFAULT_PLATFORMS.find((p) => p.label === link.platform);
  return {
    id: link.id,
    platform: match?.key || 'others',
    customPlatform: '',
    url: link.url,
  };
};

const getNameFromSectionLink = (link) => {
  if (link.platform === 'Other') {
    return (link.customPlatform || '').trim();
  }
  return link.platform;
};

const buildSociallinksPayload = (links) =>
  (links || []).map((r) => {
    const labelObj = DEFAULT_PLATFORMS.find((p) => p.key === r.platform);
    const platformLabel = labelObj ? labelObj.label : r.platform;
    return {
      platform: r.platform === 'others' ? 'Other' : platformLabel,
      customPlatform: r.platform === 'others' ? (r.customPlatform || '') : '',
      url: r.url,
    };
  });

const getSavedSocialLinks = (links) =>
  buildSociallinksPayload(
    (links || []).filter((l) => l.id).map(sectionToStorageFormat)
  );

const formatEventDates = (appointmentDate) => {
  if (!appointmentDate) return '';
  if (Array.isArray(appointmentDate)) {
    return appointmentDate
      .filter(Boolean)
      .map((d) => returnDate(d))
      .join(', ');
  }
  return returnDate(appointmentDate);
};

function AssignFeedback({ onClose, onCreated }) {
  // store categories from server as objects { id, category_name }
  const [allCategories, setAllCategories] = useState([]);
  const [customersData, setCustomersData] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userRowSelectionModel, setUserRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });
  const [userPage, setUserPage] = useState(0);
  const [userPageSize, setUserPageSize] = useState(10);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [selectedCategoryIds, setSelectedCategoryIds] = useState(() => new Set());
  const [showAddService, setShowAddService] = useState(false);
  const [fromDate, setFromDate] = useState(formatDate(firstDay));
  const [toDate, setToDate] = useState(formatDate(lastDay));
  const [enquiryID,setEnquiryID] = useState(null);

  const [socialLinks, setSocialLinks] = useState([emptySocialDraft()]);
  const [socialSaving, setSocialSaving] = useState(false);
  const [venue, setVenue] = useState(null);

  const sessionFactory = new StorageFactory('session');
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);

  // Fetch categories (GET)
  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API_GET_FB_CATEGORIES_DATA, { params: { user_id: userID } });
      const resp = res.data;

      if (resp.status_code === 200 && Array.isArray(resp.data)) {
        // store `{ id, category_name }` so delete can use id
        const parsed = resp.data.map((cls) => ({
          id: cls.id,
          category_name: cls.category_name || '',
        }));
        setAllCategories(parsed);
        setSelectedCategoryIds(new Set(parsed.map((c) => c.id)));
      } else {
        toast.error(resp.message || 'Unexpected response from server');
        setAllCategories([]);
        setSelectedCategoryIds(new Set());
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      toast.error('Failed to load services');
      setAllCategories([]);
      setSelectedCategoryIds(new Set());
    } finally {
      setLoading(false);
    }
  };

  const fetchSocialMediaLinks = async () => {
    try {
      const res = await axios.get(API_GET_SOCIAL_MEDIA_INFO, {
        params: { user_id: userID },
      });
      const data = res.data;
      if (data.status_code === 200 && Array.isArray(data.data)) {
        const saved = data.data.map(apiToSectionFormat);
        setSocialLinks([...saved, emptySocialDraft()]);
      } else {
        setSocialLinks([emptySocialDraft()]);
      }
    } catch (err) {
      console.error('Error fetching social media links:', err);
      setSocialLinks([emptySocialDraft()]);
    }
  };

  // Fetch customers (kept as your original)
  const fetchCustomersData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ user_id: userID });
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);

      const url = `${API_GET_SETTLED_CUSTOMERS}?${params.toString()}`;
      const res = await fetch(url);
      const resp = await res.json();

      if (resp.status_code === 200 && Array.isArray(resp.data)) {
        setVenue(resp.venue || null);
        const extractedData = resp.data.map((item, id) => {
          const info = item?.enquiry_info?.[0] || {};
          const countryCode = info.country_code || item.country_code || '';
          const phoneNo = info.phone_no || item.phone_no || '';
          return {
            id: id + 1,
            user_id: info.user_id || '',
            name: info.name || item.name || '',
            email: info.email || item.email || '',
            country_code: countryCode,
            phone_no: phoneNo,
            phone: item.phone || [countryCode, phoneNo].filter(Boolean).join(' '),
            address: info.address || item.address || '',
            notes: info.notes || item.notes || '',
            event_dates: item.event_dates || formatEventDates(info.appointment_date),
            enquiry_id: info.appointment_id || info.enquiry_id || item.enquiry_id || '',
            invoice_id: item.invoice_id || '',
            grand_total_amount: item.grand_total_amount,
            final_payable_amount: item.final_payable_amount,
            pending_amount: item.pending_amount,
            is_invoice_clear: item.is_invoice_clear,
            enquiry_info: Array.isArray(item?.enquiry_info)
              ? item.enquiry_info
              : info && Object.keys(info).length
                ? [info]
                : [],
          };
        });
        setCustomersData(extractedData);
      } else {
        toast.error(resp.message || 'Unexpected response from server');
        setCustomersData([]);
        setVenue(null);
      }
    } catch (err) {
      console.error('Error fetching customers:', err);
      toast.error('Failed to load customers');
      setCustomersData([]);
      setVenue(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchCustomersData();
    fetchSocialMediaLinks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchCustomersData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  const filteredUsers = customersData.filter(
    (user) =>
      (user.name || '').toLowerCase().includes((searchTerm || '').toLowerCase()) ||
      (user.email || '').toLowerCase().includes((searchTerm || '').toLowerCase())
  );

  const userColumns = useMemo(
    () => [
      { field: 'name', headerName: 'Name', flex: 1, minWidth: 140 },
      { field: 'email', headerName: 'Email', flex: 1.2, minWidth: 180 },
      { field: 'phone', headerName: 'Phone', flex: 1, minWidth: 130 },
      { field: 'event_dates', headerName: 'Event Date', flex: 1, minWidth: 140 },
    ],
    []
  );

  const handleRowSelectionChange = (newSelection) => {
    setUserRowSelectionModel(newSelection);
    const selectedIds = newSelection?.ids || new Set();
    const users = customersData.filter((u) => {
      const rowId = u.id;
      return (
        selectedIds.has(rowId) ||
        selectedIds.has(String(rowId)) ||
        selectedIds.has(Number(rowId))
      );
    });
    setSelectedUsers(users);
    const firstEnquiryId = users.find((u) => u.enquiry_id)?.enquiry_id;
    setEnquiryID(firstEnquiryId || null);
  };

  const getSelectedServiceNames = () =>
    allCategories
      .filter((c) => selectedCategoryIds.has(c.id))
      .map((c) => c.category_name);

  const toggleServiceSelection = (categoryId) => {
    setSelectedCategoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const addCategoryToServer = async () => {
    const trimmed = (newCategory || '').trim();
    if (!trimmed) {
      toast.info('Please enter a service name');
      return;
    }

    const exists = allCategories.some(
      (c) => (c.category_name || '').toLowerCase() === trimmed.toLowerCase()
    );
    if (exists) {
      toast.info('This service already exists');
      return;
    }

    setLoading(true);
    try {
      const payload = { category_name: trimmed, user_id: userID };
      const res = await axios.post(API_ADD_UPDATE_FB_CATEGORY, payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      const data = res.data;
      if (data.status_code === 200) {
        toast.success('Service added');
        setNewCategory('');
        setShowAddService(false);
        await fetchData();
        if (data.data?.id) {
          setSelectedCategoryIds((prev) => new Set([...prev, data.data.id]));
        }
      } else {
        toast.error(data.message || 'Failed to add service');
      }
    } catch (err) {
      console.error('Error adding service:', err);
      toast.error('Error adding service');
    } finally {
      setLoading(false);
    }
  };

  const deleteCategoryFromServer = async (categoryObj) => {
    if (!categoryObj || !categoryObj.id) {
      setAllCategories((prev) => prev.filter((c) => c.category_name !== categoryObj.category_name));
      return;
    }

    const confirm = window.confirm(
      `Are you sure you want to remove "${categoryObj.category_name}" from the list?`
    );
    if (!confirm) return;

    setLoading(true);
    try {
      const res = await axios.get(API_DELETE_FB_CATEGORY_ID, {
        params: { user_id: userID, id: categoryObj.id },
      });
      const data = res.data;
      if (data.status_code === 200) {
        toast.success('Service removed');
        setSelectedCategoryIds((prev) => {
          const next = new Set(prev);
          next.delete(categoryObj.id);
          return next;
        });
        await fetchData();
      } else {
        toast.error(data.message || 'Failed to delete service');
      }
    } catch (err) {
      console.error('Error deleting service:', err);
      toast.error('Error deleting service');
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLinkChange = (idx, field, value) => {
    setSocialLinks((prev) => {
      if (prev[idx]?.id) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const saveDraftSocialLink = async () => {
    const draft = [...socialLinks].reverse().find((link) => !link.id);
    if (!draft) {
      setSocialLinks((prev) => [...prev, emptySocialDraft()]);
      return;
    }

    const trimmedUrl = (draft.url || '').trim();
    const trimmedCustom = (draft.customPlatform || '').trim();
    const name = getNameFromSectionLink(draft);

    if (!trimmedUrl) {
      toast.info('Please enter a URL');
      return;
    }
    if (!isValidUrl(trimmedUrl)) {
      toast.error('Please enter a valid URL (include http:// or https://)');
      return;
    }
    if (draft.platform === 'Other' && !trimmedCustom) {
      toast.info('Please enter a custom platform name');
      return;
    }

    const exists = socialLinks.some(
      (r) =>
        r.id &&
        r.platform === draft.platform &&
        r.url.toLowerCase() === trimmedUrl.toLowerCase() &&
        (r.customPlatform || '').toLowerCase() === trimmedCustom.toLowerCase()
    );
    if (exists) {
      toast.info('This link already exists');
      return;
    }

    setSocialSaving(true);
    try {
      const res = await axios.post(
        API_ADD_SOCIAL_MEDIA_INFO,
        { user_id: userID, name, link: trimmedUrl },
        { headers: { 'Content-Type': 'application/json' } }
      );
      const data = res.data;
      if (data.status_code === 200) {
        toast.success('Social link saved');
        await fetchSocialMediaLinks();
      } else {
        toast.error(data.message || 'Failed to add link');
      }
    } catch (err) {
      console.error('Error adding social media link:', err);
      toast.error(err?.response?.data?.message || 'Failed to add link');
    } finally {
      setSocialSaving(false);
    }
  };

  const removeSocialLink = async (idx) => {
    const link = socialLinks[idx];
    if (!link) return;

    if (!link.id) {
      setSocialLinks((prev) => prev.filter((_, i) => i !== idx));
      return;
    }

    const displayName = getNameFromSectionLink(link);
    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${displayName}" link?`
    );
    if (!confirmDelete) return;

    setSocialSaving(true);
    try {
      const res = await axios.get(API_DELETE_SOCIAL_MEDIA_INFO, {
        params: { user_id: userID, id: link.id },
      });
      const data = res.data;
      if (data.status_code === 200) {
        toast.success('Social link removed');
        await fetchSocialMediaLinks();
      } else {
        toast.error(data.message || 'Failed to delete link');
      }
    } catch (err) {
      console.error('Error deleting social media link:', err);
      toast.error(err?.response?.data?.message || 'Failed to delete link');
    } finally {
      setSocialSaving(false);
    }
  };

  const handleCreateTemplate = () => {
    const draft = [...socialLinks].reverse().find((link) => !link.id);
    if (draft) {
      const trimmedUrl = (draft.url || '').trim();
      const trimmedCustom = (draft.customPlatform || '').trim();

      if (!trimmedUrl) {
        toast.info('Please enter a Profile/Page Link');
        return;
      }
      if (!isValidUrl(trimmedUrl)) {
        toast.error('Please enter a valid URL (include http:// or https://)');
        return;
      }
      if (draft.platform === 'Other' && !trimmedCustom) {
        toast.info('Please enter a custom platform name');
        return;
      }
    }

    setIsDialogOpen(true);
  };

  const handleSendFeedback = async () => {
    const selectedServices = getSelectedServiceNames();

    if (selectedUsers.length === 0 || selectedServices.length === 0) {
      toast.error('Select at least one service to rate and one user');
      return;
    }

    const enquiryInfo = selectedUsers.flatMap((user) =>
      Array.isArray(user.enquiry_info)
        ? user.enquiry_info
        : user.enquiry_info
          ? [user.enquiry_info]
          : []
    );

    setLoading(true);
    const reqbody = {
      user_id: userID,
      selectedUsers: selectedUsers,
      selectedCategories: selectedServices,
      sociallinks: getSavedSocialLinks(socialLinks),
      enquiry_info: enquiryInfo,
      venue,
    };

    try {
      const resp = await axios.post(API_SUBMIT_FEEDBACK, reqbody, {
        headers: { 'Content-Type': 'application/json' },
      });
      const respData = resp.data;
      setLoading(false);
      if (respData.status_code === 200) {
        toast.success('Feedback link sent successfully');
        onCreated?.();
        onClose();
      } else {
        toast.error(respData.message || 'Failed to send feedback');
      }
    } catch (err) {
      setLoading(false);
      console.error('Submit error', err);
      toast.error('Something went wrong.');
    }
  };

  return (
    <Box>
      <TitleWithBack title="Create Feedback" onBackClick={onClose} />

        <Box>
           <CompanyLogoUploader  enquiry_id={enquiryID}/>
        </Box>

      <Stack spacing={3} mt={2}>

        {/* List Services to Rate */}
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ py: 2.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: { xs: 1.5, sm: 2 },
              }}
            >
              <MUITypography
                variant="h3"
                fontWeight={700}
                sx={{
                  color: '#1e3a5f',
                  minWidth: { xs: 120, sm: 168 },
                  pt: 1,
                  flexShrink: 0,
                }}
              >
                List Services to Rate:
              </MUITypography>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                {allCategories.length === 0 && !showAddService && (
                  <MUITypography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    No services added yet.
                  </MUITypography>
                )}

                <Stack spacing={1.25}>
                  {allCategories.map((category) => (
                    <Box
                      key={category.id || category.category_name}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={selectedCategoryIds.has(category.id)}
                        onChange={() => toggleServiceSelection(category.id)}
                        sx={{ p: 0.5, flexShrink: 0 }}
                      />
                      <TextField
                        size="small"
                        fullWidth
                        value={category.category_name}
                        InputProps={{ readOnly: true }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: '#fff',
                            borderRadius: 1,
                            '& fieldset': { borderColor: '#c5d0de' },
                            '& .MuiInputBase-input': {
                              color: '#1e3a5f',
                              cursor: 'default',
                              textTransform: 'capitalize',
                            },
                          },
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => deleteCategoryFromServer(category)}
                        aria-label={`Remove ${category.category_name}`}
                        sx={{
                          flexShrink: 0,
                          width: 36,
                          height: 36,
                          borderRadius: 1,
                          bgcolor: '#e8eef5',
                          color: '#4a5f7a',
                          '&:hover': { bgcolor: '#dce4ef' },
                        }}
                      >
                        <Close sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  ))}

                  {showAddService && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Box sx={{ width: 34, flexShrink: 0 }} />
                      <TextField
                        size="small"
                        fullWidth
                        autoFocus
                        placeholder="Enter service name"
                        value={newCategory}
                        onChange={(e) => setNewCategory(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') addCategoryToServer();
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: '#fff',
                            borderRadius: 1,
                            '& fieldset': { borderColor: '#c5d0de' },
                          },
                        }}
                      />
                      <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={addCategoryToServer}
                          sx={{ minWidth: 56 }}
                        >
                          Add
                        </Button>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setShowAddService(false);
                            setNewCategory('');
                          }}
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 1,
                            bgcolor: '#e8eef5',
                            color: '#4a5f7a',
                          }}
                        >
                          <Close sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Stack>
                    </Box>
                  )}
                </Stack>

                {!showAddService && (
                  <Button
                    variant="text"
                    color="primary"
                    onClick={() => setShowAddService(true)}
                    sx={{
                      mt: 1,
                      px: 0,
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      justifyContent: 'flex-start',
                    }}
                  >
                    + Add Another Service
                  </Button>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>


        {/* Users Card */}
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <People color="primary" />
                <MUITypography variant="h4" fontWeight={600}>
                  Select Customer
                </MUITypography>
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField
                  type="date"
                  size="small"
                  label="From"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  type="date"
                  size="small"
                  label="To"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>

            </Box>
            <MUITypography variant="body2" color="text.secondary" mb={3}>
              Choose Customers To Send Feedback Requests
            </MUITypography>

            <TextField
              fullWidth
              placeholder="Search users by name or email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 1,
                backgroundColor: '#fff',
                borderRadius: 1,
                boxShadow: 1,
              }}
            />
           {selectedUsers.length > 0 && (
              <Alert severity="info" icon={<CheckCircle />} sx={{ mt: 2 ,mb:2}}>
                <MUITypography variant="body2">
                  {selectedUsers.length} user(s) selected
                </MUITypography>
              </Alert>
            )}
            <ReusableDataGrid
              rows={filteredUsers}
              columns={userColumns}
              loading={loading}
              paginationMode="client"
              rowCount={filteredUsers.length}
              page={userPage}
              pageSize={userPageSize}
              onPageChange={setUserPage}
              onPageSizeChange={(size) => {
                setUserPageSize((prev) => {
                  if (prev !== size) {
                    setUserPage(0);
                    return size;
                  }
                  return prev;
                });
              }}
              pageSizeOptions={[5, 10, 25, 50]}
              checkboxSelection
              disableRowSelectionOnClick
              keepNonExistentRowsSelected
              disableRowSelectionExcludeModel
              rowSelectionModel={userRowSelectionModel}
              onRowSelectionModelChange={handleRowSelectionChange}
              autoHeight
              customSx={{
                ...dataGridStyle,
                '& .MuiDataGrid-virtualScroller': {
                  overflow: 'hidden !important',
                },
                '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
                  display: 'none',
                },
                '& .MuiDataGrid-columnHeader--checkboxSelection .MuiCheckbox-root, & .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root':
                  {
                    color: '#fff',
                  },
                '& .MuiDataGrid-columnHeader--checkboxSelection .MuiCheckbox-root.Mui-checked, & .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root.Mui-checked':
                  {
                    color: '#fff',
                  },
                '& .MuiDataGrid-columnHeader--checkboxSelection .MuiCheckbox-root.MuiCheckbox-indeterminate, & .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root.MuiCheckbox-indeterminate':
                  {
                    color: '#fff',
                  },
                '& .MuiDataGrid-columnHeader--checkboxSelection .MuiCheckbox-root .MuiSvgIcon-root, & .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root .MuiSvgIcon-root':
                  {
                    color: '#fff',
                  },
              }}
            />

           
          </CardContent>
        </Card>

        <SocialLinkSection
          links={socialLinks}
          onChange={handleSocialLinkChange}
          onAdd={saveDraftSocialLink}
          onRemove={removeSocialLink}
          saving={socialSaving}
        />


        {/* Footer Buttons + Dialog */}
        <Box display="flex" justifyContent="center" mt={4} mb={2}>
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" color="secondary" onClick={() => {
              setSelectedUsers([]);
              setUserRowSelectionModel({ type: 'include', ids: new Set() });
              setEnquiryID(null);
            }}>
              Reset
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreateTemplate}
            >
              Create Template
            </Button>
          </Stack>
        </Box>
      </Stack>
      {/* {loading && <Loader loading={loading} />} */}
      <Box sx={{ position: "relative" ,...align_center}}>
        {loading ? <CircularProgress loading={loading} /> : null}
      </Box>

      {/* Feedback Popup Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <MUITypography variant='h3' sx={align_center}>
            Guest Feedback Form Template
          </MUITypography>
        </DialogTitle>
        <DialogContent dividers>
          <FeedbackTemplate
            allCategories={getSelectedServiceNames()}
            selectedUsers={
              selectedUsers.length
                ? selectedUsers
                : customersData[0]
                  ? [customersData[0]]
                  : []
            }
            sociallinks={getSavedSocialLinks(socialLinks)}
            enquiryId={
              selectedUsers[0]?.enquiry_id ||
              customersData[0]?.enquiry_id ||
              enquiryID
            }
            enquiryInfo={
              selectedUsers[0]?.enquiry_info || customersData[0]?.enquiry_info
            }
            venue={venue}
            userId={userID}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={ handleSendFeedback }
            color="primary"
            variant="contained"
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AssignFeedback;
